import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Clock, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import useSession from 'hooks/useSession';
import { cn } from '@/lib/utils';

/** Below this the countdown turns urgent, not just informative. */
const URGENT_THRESHOLD_MS = 60 * 1000;

const formatRemaining = (ms) => {
  const safe = Math.max(0, ms || 0);
  const totalSeconds = Math.ceil(safe / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Warns before a session ends, while there is still time to act.
 *
 * The report describes three-to-six-hour sessions ending in `expiredSession`
 * with no warning: a teacher partway through an evaluation, or a student mid
 * upload, simply lost the work. The machinery to see it coming already existed —
 * the session manager enters EXPIRING five minutes out — but nothing rendered
 * it, so the first sign of trouble was a failed save.
 *
 * Three distinct situations, deliberately worded differently:
 *
 *   idle       — the user has gone quiet and the session is about to close for
 *                it. The only one they can undo outright, and the only one where
 *                the answer costs nothing: a click, no request.
 *   canExtend  — ordinary expiry. One button buys more time, and the countdown
 *                is informative rather than alarming.
 *   !canExtend — the absolute ceiling. Nothing will extend it, so the message
 *                stops offering and starts telling the user to save now. The
 *                session is still usable while this shows, which is the point:
 *                warning after the token died would be useless.
 */
const SessionExpiryWarning = ({ t }) => {
  const { isEnding, isRenewing, isIdleWarning, canExtend, msRemaining, extend, stayActive } =
    useSession();
  const [dismissed, setDismissed] = React.useState(false);

  // Reset when a new warning cycle begins, so dismissing one does not silence
  // the next.
  React.useEffect(() => {
    if (!isEnding) setDismissed(false);
  }, [isEnding]);

  if (!isEnding || dismissed) return null;

  const urgent = !canExtend || (msRemaining !== null && msRemaining <= URGENT_THRESHOLD_MS);
  const remaining = formatRemaining(msRemaining);

  const copy = isIdleWarning
    ? { title: 'session.idleTitle', body: 'session.idleBody' }
    : canExtend
      ? { title: 'session.expiringTitle', body: 'session.expiringBody' }
      : { title: 'session.maxDurationTitle', body: 'session.maxDurationBody' };

  return (
    // Locked shut only while the fix lives in this dialog: with a working
    // "keep working" button, letting the user quietly dismiss it is the worst
    // outcome — they walk away believing it was handled and lose the work anyway.
    //
    // Once the ceiling is reached the dialog stops being the fix and becomes the
    // instruction "go save your work" — which a modal that blocks the whole page
    // makes impossible. So that case is dismissible, and says so.
    <AlertDialog open>
      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          // Only the ceiling case closes: for the other two the fix is the
          // button in the dialog, and dismissing it silently is how work is lost.
          if (canExtend || isIdleWarning) event.preventDefault();
          else setDismissed(true);
        }}
        className={cn(
          // On a 430px phone the shared base (`w-full max-w-sm` + `sm:rounded-lg`)
          // renders edge to edge with square corners: the max-width never binds
          // below 448px and the rounding only starts at `sm`. Both are overridden
          // here rather than in the shared component, so every other dialog in the
          // app keeps the look it has today.
          'w-[calc(100%-2rem)] max-w-md rounded-lg p-5 sm:p-6',
          // A phone in landscape — or the app inside Blackboard's iframe — leaves
          // less height than this dialog wants. Scrolling within it beats a button
          // the user cannot reach, which on this particular dialog means losing
          // the work it exists to protect.
          'max-h-[calc(100dvh-2rem)] overflow-y-auto short:gap-3 short:p-4'
        )}
      >
        <AlertDialogHeader>
          {/* justify matches the header's own `text-center sm:text-left`: the
              flex row would otherwise stay left-aligned while the description
              below it centres. */}
          <AlertDialogTitle className="flex items-center justify-center gap-2 text-base sm:justify-start sm:text-lg">
            {urgent ? (
              <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
            ) : (
              <Clock className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            {t(copy.title)}
          </AlertDialogTitle>

          <AlertDialogDescription>
            {t(copy.body)}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div
          className={cn(
            'flex items-baseline justify-center gap-2 py-2',
            urgent ? 'text-destructive' : 'text-foreground'
          )}
        >
          {/* Announced politely: a per-second assertive update would make the
              dialog unusable with a screen reader. */}
          <span className="text-3xl font-semibold tabular-nums" role="timer" aria-live="polite">
            {remaining}
          </span>
          <span className="text-sm text-muted-foreground">{t('session.remaining')}</span>
        </div>

        <AlertDialogFooter>
          {isIdleWarning ? (
            // No mutation behind this one: it records presence locally and
            // re-derives the schedule, so answering costs nothing and cannot fail.
            <Button onClick={stayActive} className="w-full">
              {t('session.stayConnected')}
            </Button>
          ) : canExtend ? (
            <Button onClick={extend} disabled={isRenewing} className="w-full">
              {isRenewing ? t('session.extending') : t('session.extend')}
            </Button>
          ) : (
            <div className="w-full space-y-3">
              <p className="text-center text-sm text-muted-foreground">
                {t('session.saveNow')}
              </p>
              {/* `secondary`, not `outline`: this project's Button defines
                  default/destructive/secondary/ghost/link, so `outline` matched
                  nothing and cva emitted no variant classes — the button rendered
                  as bare bold text with no border or background. */}
              <Button variant="secondary" onClick={() => setDismissed(true)} className="w-full">
                {t('session.dismissWarning')}
              </Button>
            </div>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

SessionExpiryWarning.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('common')(SessionExpiryWarning);
